import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, passwordResetRequestSchema, passwordSchema } from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { AuthLayout, usePublicBranding } from '@/components/layout/auth-layout';
import { LoginSecondFactor } from '@/components/two-factor';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/form-field';
import { Alert, Input } from '@/components/ui/primitives';
import { api, ApiError, errorMessage, getTenantSlug } from '@/lib/api';
import { ME_KEY, useAuth } from '@/lib/auth';

const SSO_ERRORS: Record<string, string> = {
  unknown_user: 'Für dieses Microsoft-Konto gibt es keinen aktiven Zugang. Bitte wenden Sie sich an Ihre Administration.',
  cancelled: 'Die Anmeldung mit Microsoft wurde abgebrochen.',
  disabled: 'Die Anmeldung mit Microsoft ist nicht eingerichtet.',
  locked: 'Das Konto ist vorübergehend gesperrt.',
  no_email: 'Das Microsoft-Konto liefert keine E-Mail-Adresse.',
};

function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 21 21" className="size-4" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export function LoginPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const { data: branding } = usePublicBranding();
  const form = useForm<z.infer<typeof loginSchema>>({ resolver: zodResolver(loginSchema), defaultValues: { email: '', password: '' } });

  // Zweiter Faktor: Antwort 200 mit challenge statt Sitzung
  const [mfa, setMfa] = useState<{ mfa: 'verify' | 'setup'; challenge: string } | null>(null);
  const finish = async () => {
    await qc.invalidateQueries({ queryKey: ME_KEY });
    navigate(from, { replace: true });
  };
  const mutation = useMutation({
    mutationFn: (values: z.infer<typeof loginSchema>) => api<{ mfa: 'verify' | 'setup'; challenge: string } | undefined>('/auth/login', { method: 'POST', body: values }),
    onSuccess: async (res) => {
      if (res?.mfa) {
        setMfa(res);
        return;
      }
      await finish();
    },
  });

  const [params] = useSearchParams();
  const ssoError = params.get('sso_error');

  if (me && !mfa) return <Navigate to={from} replace />;
  if (mfa) {
    return (
      <AuthLayout title={mfa.mfa === 'verify' ? 'Bestätigung' : 'Zwei-Faktor-Anmeldung einrichten'} description={mfa.mfa === 'verify' ? 'Geben Sie den Code aus Ihrer Authenticator-App ein.' : undefined}>
        <LoginSecondFactor
          mfa={mfa.mfa}
          challenge={mfa.challenge}
          onDone={finish}
          onCancel={() => {
            setMfa(null);
            form.setValue('password', '');
          }}
        />
      </AuthLayout>
    );
  }

  const slug = getTenantSlug();
  // Ziel (z. B. Link aus einer E-Mail) durch die Microsoft-Anmeldung mitnehmen
  const ssoParams = new URLSearchParams({ ...(slug ? { tenant: slug } : {}), ...(from !== '/' ? { returnTo: from } : {}) }).toString();
  const microsoftUrl = `/api/auth/m365/start${ssoParams ? `?${ssoParams}` : ''}`;

  return (
    <AuthLayout title="Anmelden" description="Melden Sie sich mit Ihrer E-Mail-Adresse an.">
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate>
        {mutation.isError && <Alert variant="destructive">{errorMessage(mutation.error)}</Alert>}
        {ssoError && !mutation.isError && <Alert variant="destructive">{SSO_ERRORS[ssoError] ?? 'Die Anmeldung mit Microsoft ist fehlgeschlagen.'}</Alert>}
        {branding?.microsoftLogin && (
          <>
            <Button type="button" variant="outline" asChild>
              <a href={microsoftUrl}>
                <MicrosoftLogo /> Mit Microsoft anmelden
              </a>
            </Button>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" /> oder mit Passwort <span className="h-px flex-1 bg-border" />
            </div>
          </>
        )}
        <Field label="E-Mail-Adresse" htmlFor="email" error={form.formState.errors.email?.message}>
          <Input id="email" type="email" autoComplete="username" autoFocus {...form.register('email')} />
        </Field>
        <Field label="Passwort" htmlFor="password" error={form.formState.errors.password?.message}>
          <Input id="password" type="password" autoComplete="current-password" {...form.register('password')} />
        </Field>
        <Button type="submit" loading={mutation.isPending} className="mt-1">
          Anmelden
        </Button>
        <Link to="/forgot-password" className="text-center text-sm text-muted-foreground hover:text-foreground">
          Passwort vergessen?
        </Link>
        {branding?.portalSelfRegistration && (
          <p className="border-t pt-4 text-center text-sm text-muted-foreground">
            Noch kein Kundenkonto?{' '}
            <Link to="/register" className="font-medium text-primary hover:underline">
              Jetzt registrieren
            </Link>
          </p>
        )}
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const form = useForm<{ email: string }>({ resolver: zodResolver(passwordResetRequestSchema), defaultValues: { email: '' } });
  const mutation = useMutation({ mutationFn: (v: { email: string }) => api('/auth/password/forgot', { method: 'POST', body: v }) });

  return (
    <AuthLayout title="Passwort vergessen" description="Wir senden Ihnen einen Link zum Zurücksetzen.">
      {mutation.isSuccess ? (
        <div className="flex flex-col gap-4">
          <Alert variant="success">
            Falls ein Konto mit dieser Adresse existiert, wurde eine E-Mail mit weiteren Schritten versendet.
          </Alert>
          <Button asChild variant="outline">
            <Link to="/login">Zur Anmeldung</Link>
          </Button>
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate>
          {mutation.isError && <Alert variant="destructive">{errorMessage(mutation.error)}</Alert>}
          <Field label="E-Mail-Adresse" htmlFor="email" error={form.formState.errors.email?.message}>
            <Input id="email" type="email" autoComplete="email" autoFocus {...form.register('email')} />
          </Field>
          <Button type="submit" loading={mutation.isPending}>
            Link anfordern
          </Button>
          <Link to="/login" className="text-center text-sm text-muted-foreground hover:text-foreground">
            Zurück zur Anmeldung
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}

type TokenInfo =
  | { valid: false }
  | { valid: true; type: 'INVITATION' | 'PASSWORD_RESET' | 'EMAIL_VERIFICATION'; email: string; firstName: string; expiresAt: string };

const setPasswordForm = z
  .object({ password: passwordSchema, confirm: z.string() })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'Die Passwörter stimmen nicht überein' });

export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const info = useQuery({
    queryKey: ['token', token],
    queryFn: () => api<TokenInfo>('/auth/token/inspect', { method: 'POST', body: { token } }),
    enabled: token.length >= 20,
    retry: false,
  });
  const form = useForm<z.infer<typeof setPasswordForm>>({ resolver: zodResolver(setPasswordForm), defaultValues: { password: '', confirm: '' } });
  const mutation = useMutation({
    mutationFn: (v: z.infer<typeof setPasswordForm>) => api('/auth/password/reset', { method: 'POST', body: { token, password: v.password } }),
  });

  const isInvite = info.data?.valid && info.data.type === 'INVITATION';
  const title = isInvite ? 'Willkommen!' : 'Neues Passwort festlegen';

  if (mutation.isSuccess) {
    return (
      <AuthLayout title="Passwort gespeichert">
        <div className="flex flex-col gap-4">
          <Alert variant="success">Ihr Passwort wurde gespeichert. Sie können sich jetzt anmelden.</Alert>
          <Button asChild>
            <Link to="/login">Zur Anmeldung</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={title}
      description={info.data?.valid ? `${info.data.firstName}, legen Sie ein Passwort für ${info.data.email} fest.` : undefined}
    >
      {info.isLoading ? (
        <p className="text-sm text-muted-foreground">Link wird geprüft …</p>
      ) : !token || info.isError || !info.data?.valid || info.data.type === 'EMAIL_VERIFICATION' ? (
        <div className="flex flex-col gap-4">
          <Alert variant="destructive">
            Der Link ist ungültig, abgelaufen oder wurde bereits verwendet. Bitte nehmen Sie den Link aus der neuesten E-Mail oder fordern Sie einen neuen an. Ist Microsoft 365 eingerichtet, können sich Mitarbeitende auch direkt mit „Mit Microsoft anmelden“ anmelden.
          </Alert>
          <Button asChild variant="outline">
            <Link to="/forgot-password">Neuen Link anfordern</Link>
          </Button>
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate>
          {mutation.isError && <Alert variant="destructive">{errorMessage(mutation.error)}</Alert>}
          <Field
            label="Neues Passwort"
            htmlFor="password"
            error={form.formState.errors.password?.message}
            hint="Mindestens 10 Zeichen, Groß- und Kleinbuchstaben sowie eine Ziffer oder ein Sonderzeichen."
          >
            <Input id="password" type="password" autoComplete="new-password" autoFocus {...form.register('password')} />
          </Field>
          <Field label="Passwort wiederholen" htmlFor="confirm" error={form.formState.errors.confirm?.message}>
            <Input id="confirm" type="password" autoComplete="new-password" {...form.register('confirm')} />
          </Field>
          <Button type="submit" loading={mutation.isPending}>
            Passwort speichern
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'pending' | 'ok' | 'error'>('pending');
  const [message, setMessage] = useState('');

  // Einmal-Token: nur einmal senden (auch im React-StrictMode).
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api('/auth/email/verify', { method: 'POST', body: { token } })
      .then(() => setState('ok'))
      .catch((e) => {
        setState('error');
        setMessage(e instanceof ApiError ? e.message : 'Unbekannter Fehler');
      });
  }, [token]);

  return (
    <AuthLayout title="E-Mail-Adresse bestätigen">
      {state === 'pending' && <p className="text-sm text-muted-foreground">Wird bestätigt …</p>}
      {state === 'ok' && (
        <div className="flex flex-col items-center gap-4 text-center">
          <CheckCircle2 className="size-10 text-success" />
          <p className="text-sm">Ihre E-Mail-Adresse wurde bestätigt.</p>
          <Button asChild className="w-full">
            <Link to="/">Weiter</Link>
          </Button>
        </div>
      )}
      {state === 'error' && <Alert variant="destructive">{message}</Alert>}
    </AuthLayout>
  );
}
